'use client'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { type ComponentProps } from 'react'

import { cn } from '@/shared/lib'

import { Icon } from '../icon'

export const Root = ({
  ...props
}: ComponentProps<typeof DialogPrimitive.Root>) => {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

export const Trigger = ({
  ...props
}: ComponentProps<typeof DialogPrimitive.Trigger>) => {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

export const Portal = ({
  ...props
}: ComponentProps<typeof DialogPrimitive.Portal>) => {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

export const Close = ({
  ...props
}: ComponentProps<typeof DialogPrimitive.Close>) => {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

export const Overlay = ({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Overlay>) => {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'z-dialog fixed inset-0 bg-black/50',
        'data-[state=open]:animate-in data-[state=open]:fade-in-0',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        className,
      )}
      {...props}
    />
  )
}

type ContentProps = ComponentProps<typeof DialogPrimitive.Content> & {
  closeButton?: boolean
  onClose?: () => void
}

export const Content = ({
  className,
  children,
  closeButton = true,
  onClose,
  ...props
}: ContentProps) => {
  return (
    <Portal data-slot="dialog-portal">
      <Overlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'z-dialog fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]',
          'bg-background flex w-[400px] max-w-[calc(100%-2rem)] flex-col gap-6 rounded-xl p-6 duration-200',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          className,
        )}
        {...props}
      >
        {children}
        {closeButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent absolute top-6.5 right-6 cursor-pointer rounded-xs focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none"
            onClick={onClose}
          >
            <Icon
              name="close"
              size={24}
              strokeWidth={1.5}
              className="pointer-events-none shrink-0 text-black"
            />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </Portal>
  )
}

export const Header = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-6', className)}
      {...props}
    />
  )
}

export const Footer = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2', className)}
      {...props}
    />
  )
}

export const Title = ({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>) => {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('typo-headline2 font-semibold text-black', className)}
      {...props}
    />
  )
}

export const Description = ({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) => {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('typo-body1 max-h-18 text-black', className)}
      {...props}
    />
  )
}
